import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, Code2, Server, ShieldCheck, Network, Wrench, GraduationCap,
  Smartphone, Cpu, Star, CheckCircle2, MapPin, Sparkles,
} from "lucide-react";
import api from "@/lib/api";
import { openWhatsApp } from "@/lib/whatsapp";
import { Reveal, Overline, SectionTitle, PrimaryLink } from "@/components/public/ui";

const ICONS = { code: Code2, smartphone: Smartphone, network: Network, shield: ShieldCheck, wrench: Wrench, server: Server, "graduation-cap": GraduationCap };
const SECTORS = ["Banque & Finance", "Distribution & Commerce", "Santé", "BTP & Immobilier", "Éducation", "Transport & Logistique"];

export default function Home() {
  const [solutions, setSolutions] = useState([]);
  const [services, setServices] = useState([]);
  const [products, setProducts] = useState([]);
  const [posts, setPosts] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [cx, setCx] = useState({});
  const h = (k, fb) => cx[k] || fb;

  useEffect(() => {
    api.get("/pages/accueil").then(({ data }) => setCx(data.sections || {})).catch(() => {});
    api.get("/solutions").then(({ data }) => setSolutions(data.slice(0, 3))).catch(() => {});
    api.get("/services").then(({ data }) => setServices(data.slice(0, 6))).catch(() => {});
    api.get("/products").then(({ data }) => setProducts(data.filter((p) => p.featured).slice(0, 3))).catch(() => {});
    api.get("/blog_posts").then(({ data }) => setPosts(data.slice(0, 3))).catch(() => {});
    api.get("/testimonials").then(({ data }) => setTestimonials(data.slice(0, 3))).catch(() => {});
  }, []);

  return (
    <div className="overflow-hidden">
      {/* HERO */}
      <section className="relative">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0F2A6B] via-[#0A1F52] to-[#071640]" />
        <div className="absolute inset-0 grain" />
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-[#4CB944]/20 blur-3xl" />
        <div className="absolute top-1/2 -left-32 w-96 h-96 rounded-full bg-[#1E40AF]/30 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 pt-20 pb-24 lg:pt-28 lg:pb-32 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 text-white">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 border border-white/15 backdrop-blur-sm mb-6">
              <MapPin className="w-3.5 h-3.5 text-[#4CB944]" />
              <span className="text-xs font-mono tracking-wide">{h("hero_badge", "Abidjan · Cocody Riviera 3 · Côte d'Ivoire")}</span>
            </motion.div>
            <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }}
              className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] font-head">
              {h("hero_title", "Transformer vos besoins en")} <span className="text-[#4CB944]">{h("hero_highlight", "solutions technologiques")}</span> {h("hero_title_end", "performantes.")}
            </motion.h1>
            <motion.p initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.2 }}
              className="mt-6 text-lg text-slate-300 max-w-xl leading-relaxed">
              {h("hero_subtitle", "VISION LOGICIELS accompagne entreprises, administrations et professionnels dans leur transformation numérique, leur équipement informatique et la modernisation de leurs systèmes d'information.")}
            </motion.p>
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.3 }}
              className="mt-8 flex flex-wrap gap-4">
              <PrimaryLink to="/devis" testid="hero-devis-button" className="bg-[#4CB944] hover:bg-[#3AA033] shadow-[#4CB944]/30">
                Demander un devis <ArrowRight className="w-4 h-4 ml-2" />
              </PrimaryLink>
              <button data-testid="hero-contact-advisor-button" onClick={() => openWhatsApp({ page: "hero", message: "Bonjour, je souhaite parler à un conseiller VISION LOGICIELS." })}
                className="inline-flex items-center px-6 py-3 rounded-full border border-white/25 text-white font-semibold hover:bg-white/10 transition-colors backdrop-blur-sm">
                Parler à un conseiller
              </button>
            </motion.div>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}
              className="mt-12 grid grid-cols-3 gap-6 max-w-md">
              {[[h("stat1_value", "150+"), h("stat1_label", "Projets livrés")], [h("stat2_value", "24/7"), h("stat2_label", "Support IT")], [h("stat3_value", "10+"), h("stat3_label", "Secteurs")]].map(([v, l]) => (
                <div key={l}><div className="text-3xl font-bold text-white font-head">{v}</div><div className="text-xs text-slate-400 mt-1">{l}</div></div>
              ))}
            </motion.div>
          </div>
          <motion.div initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, delay: 0.3 }}
            className="lg:col-span-5 relative">
            <div className="rounded-3xl overflow-hidden border border-white/10 shadow-2xl rotate-1 hover:rotate-0 transition-transform duration-500">
              <img src={h("hero_image", "https://images.unsplash.com/photo-1573164574397-dd250bc8a598?w=900&q=80")} alt="Équipe VISION LOGICIELS" className="w-full h-[420px] object-cover" />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl p-4 flex items-center gap-3 max-w-[220px]">
              <div className="w-11 h-11 rounded-xl bg-[#4CB944]/15 grid place-items-center"><Sparkles className="w-6 h-6 text-[#4CB944]" /></div>
              <div><div className="text-sm font-bold text-slate-900">Vision POS · ERP · CRM</div><div className="text-xs text-slate-500">Logiciels de gestion</div></div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* SOLUTIONS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20 lg:py-28">
        <SectionTitle eyebrow="Nos solutions logicielles" title="Des logiciels pensés pour votre performance"
          subtitle="Découvrez notre gamme de solutions développées et commercialisées par VISION LOGICIELS." />
        <div className="grid md:grid-cols-3 gap-6">
          {solutions.map((s, i) => (
            <Reveal key={s.id} delay={i * 0.1}>
              <Link to={`/solutions/${s.slug}`} data-testid={`solution-card-${s.slug}`}
                className="group block h-full rounded-2xl bg-white border border-slate-200 overflow-hidden hover:shadow-xl hover:shadow-slate-200/60 hover:-translate-y-1 transition-all">
                <div className="h-44 overflow-hidden"><img src={s.image} alt={s.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /></div>
                <div className="p-6">
                  <div className="text-xs font-mono text-[#4CB944] uppercase tracking-wider mb-2">{s.category}</div>
                  <h3 className="text-xl font-bold text-slate-900 font-head">{s.name}</h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed line-clamp-2">{s.short_desc}</p>
                  <div className="mt-4 flex items-center justify-between">
                    <span className="text-sm font-semibold text-[#0F2A6B]">{s.price}</span>
                    <span className="text-sm font-medium text-slate-400 group-hover:text-[#4CB944] flex items-center gap-1 transition-colors">Découvrir <ArrowRight className="w-4 h-4" /></span>
                  </div>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* SERVICES */}
      <section className="bg-white border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-20 lg:py-28">
          <SectionTitle eyebrow="Nos services" title="Un partenaire IT complet" center
            subtitle="Du développement à la maintenance, nous couvrons l'ensemble de vos besoins technologiques." />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((s, i) => {
              const Icon = ICONS[s.icon] || Cpu;
              return (
                <Reveal key={s.id} delay={i * 0.06}>
                  <Link to={`/services/${s.slug}`} data-testid={`service-card-${s.slug}`}
                    className="group block h-full p-6 rounded-2xl border border-slate-200 hover:border-[#0F2A6B]/30 hover:shadow-lg transition-all bg-[#FAFAF9]">
                    <div className="w-12 h-12 rounded-xl bg-[#0F2A6B] grid place-items-center mb-4 group-hover:bg-[#4CB944] transition-colors">
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-900 font-head">{s.name}</h3>
                    <p className="mt-2 text-sm text-slate-600 leading-relaxed">{s.short_desc}</p>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* HARDWARE */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20 lg:py-28">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-12">
          <div className="max-w-xl"><Overline>Catalogue Hardware</Overline>
            <h2 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 font-head">Matériel informatique de confiance</h2></div>
          <PrimaryLink to="/produits" testid="hardware-all-button" className="!px-5 !py-2.5 text-sm">Voir le catalogue</PrimaryLink>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {products.map((p, i) => (
            <Reveal key={p.id} delay={i * 0.1}>
              <Link to={`/produits/${p.slug}`} data-testid={`product-card-${p.slug}`}
                className="group block h-full rounded-2xl bg-white border border-slate-200 overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-all">
                <div className="relative h-48 bg-slate-50 overflow-hidden">
                  <img src={p.image} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  {p.featured && <span className="absolute top-3 left-3 px-3 py-1 rounded-full bg-[#4CB944] text-white text-xs font-semibold">Vedette</span>}
                </div>
                <div className="p-5">
                  <div className="text-xs font-mono text-slate-400 uppercase">{p.brand} · {p.category}</div>
                  <h3 className="mt-1 text-base font-semibold text-slate-900">{p.name}</h3>
                  <div className="mt-3 text-[#0F2A6B] font-bold">{p.price}</div>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* SECTORS */}
      <section className="bg-[#0A1F52] relative grain overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-20 relative">
          <SectionTitle eyebrow="Secteurs d'activité" title="Nous connaissons votre métier" center
            subtitle="Nos solutions s'adaptent aux exigences spécifiques de chaque secteur." />
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {SECTORS.map((sec, i) => (
              <Reveal key={sec} delay={i * 0.05}>
                <div className="flex items-center gap-3 p-5 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 transition-colors">
                  <CheckCircle2 className="w-5 h-5 text-[#4CB944] shrink-0" />
                  <span className="text-white font-medium">{sec}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      {testimonials.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20 lg:py-28">
          <SectionTitle eyebrow="Témoignages" title="Ils nous font confiance" center />
          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <Reveal key={t.id} delay={i * 0.1}>
                <div className="h-full p-6 rounded-2xl bg-white border border-slate-200 shadow-sm">
                  <div className="flex gap-1 mb-4">{Array.from({ length: t.rating || 5 }).map((_, k) => <Star key={k} className="w-4 h-4 fill-[#4CB944] text-[#4CB944]" />)}</div>
                  <p className="text-slate-700 leading-relaxed italic">“{t.text}”</p>
                  <div className="mt-5 pt-4 border-t border-slate-100">
                    <div className="font-semibold text-slate-900">{t.name}</div>
                    <div className="text-sm text-slate-500">{t.role} · {t.company}</div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {/* BLOG */}
      {posts.length > 0 && (
        <section className="bg-white border-y border-slate-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-20">
            <div className="flex flex-wrap items-end justify-between gap-4 mb-12">
              <div><Overline>Blog & Actualités</Overline><h2 className="mt-3 text-3xl sm:text-4xl font-bold text-slate-900 font-head">Articles récents</h2></div>
              <PrimaryLink to="/blog" testid="blog-all-button" className="!px-5 !py-2.5 text-sm">Tous les articles</PrimaryLink>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {posts.map((p, i) => (
                <Reveal key={p.id} delay={i * 0.1}>
                  <Link to={`/blog/${p.slug}`} data-testid={`blog-card-${p.slug}`} className="group block rounded-2xl overflow-hidden border border-slate-200 hover:shadow-lg transition-all">
                    <div className="h-44 overflow-hidden"><img src={p.cover} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /></div>
                    <div className="p-5">
                      <span className="text-xs font-mono text-[#4CB944] uppercase">{p.category}</span>
                      <h3 className="mt-2 font-semibold text-slate-900 font-head line-clamp-2 group-hover:text-[#0F2A6B]">{p.title}</h3>
                      <p className="mt-2 text-sm text-slate-500 line-clamp-2">{p.excerpt}</p>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA FINAL */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20">
        <div className="relative rounded-3xl overflow-hidden bg-gradient-to-br from-[#0F2A6B] to-[#0A1F52] p-10 lg:p-16 text-center grain">
          <div className="absolute -top-16 -right-16 w-72 h-72 rounded-full bg-[#4CB944]/20 blur-3xl" />
          <div className="relative">
            <h2 className="text-3xl sm:text-4xl font-bold text-white font-head max-w-2xl mx-auto">{h("cta_title", "Prêt à accélérer votre transformation numérique ?")}</h2>
            <p className="mt-4 text-slate-300 max-w-xl mx-auto">{h("cta_subtitle", "Discutons de votre projet. Nos experts vous accompagnent de A à Z.")}</p>
            <div className="mt-8 flex flex-wrap gap-4 justify-center">
              <PrimaryLink to="/devis" testid="cta-devis-button" className="bg-[#4CB944] hover:bg-[#3AA033]">Demander un devis</PrimaryLink>
              <button onClick={() => openWhatsApp({ page: "cta-final" })} className="inline-flex items-center px-6 py-3 rounded-full border border-white/25 text-white font-semibold hover:bg-white/10 transition-colors">Discuter sur WhatsApp</button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
